import { useState, useEffect } from 'react';
import apiService from '../../services/api';

export default function useNotificationPanel({ isOpen }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getNotifications();
      if (response.success) {
        setNotifications(response.notifications || []);
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to fetch notifications');
      // For demo purposes, show some mock notifications
      setNotifications([
        {
          id: 1,
          title: 'New Course Content Added',
          message: 'Week 3 materials have been uploaded for AQ101 - Aqeedah Foundations',
          type: 'course_content',
          is_read: false,
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 2,
          title: 'Attendance Session Starting Soon',
          message: 'Week 2 attendance session for AQ101 will begin in 15 minutes',
          type: 'attendance',
          is_read: false,
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 3,
          title: 'Assignment Grade Posted',
          message: 'Your grade for Assignment 1 has been posted. Check your grades section.',
          type: 'grade',
          is_read: true,
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      // For demo purposes, mark as read locally
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: true }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      // For demo purposes, mark all as read locally
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: true }))
      );
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    unreadCount
  };
}
